import { type ChangeEvent, useState } from 'react';
import { useOrgStructure } from '../api/org';
import {
  CONTACT_CHANNELS,
  type Contact,
  DUTY_PERIODS,
  type KpiCommitment,
  openFile,
  type Regulation,
  type RegulationDraft,
  type RegulationFile,
  uploadFile,
} from '../api/regulations';
import { usePersonDrawer } from '../layout/shellContext';
import { Icon } from './Icon';
import { ErrorState } from './states';

const extension = (name: string) => name.split('.').pop()?.toUpperCase() ?? '';
const withoutExtension = (name: string) => name.replace(/\.[^.]+$/, '');
const formatDate = (value: string) => new Date(value).toLocaleDateString('ru-RU');

function FileChip({ file, onRemove }: { file: RegulationFile; onRemove?: () => void }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="doc-chip" title={error ?? file.name}>
      <span className="doc-chip-icon">
        <Icon name="file" />
      </span>
      {onRemove ? (
        <span className="doc-chip-name">{withoutExtension(file.name)}</span>
      ) : (
        <button
          type="button"
          className="doc-chip-name link"
          onClick={() => openFile(file).catch((reason: Error) => setError(reason.message))}
        >
          {withoutExtension(file.name)}
        </button>
      )}
      <span className="doc-chip-ext">{extension(file.name)}</span>
      {onRemove ? (
        <button
          type="button"
          className="doc-chip-remove"
          aria-label={`Убрать ${file.name}`}
          onClick={onRemove}
        >
          <Icon name="x" size={14} />
        </button>
      ) : null}
    </div>
  );
}

/** Просмотр регламента: документы строкой, правила и обязанности в две колонки. */
/**
 * Обязанности собираются в блоки по рабочей области: название области — заголовок блока,
 * под ним — что в этой области делает сотрудник. Порядок областей — как в регламенте.
 */
function groupDuties(duties: Regulation['duties']) {
  const blocks: { area: string; duties: Regulation['duties'] }[] = [];
  for (const duty of duties) {
    const area = duty.area ?? '';
    const last = blocks.find((block) => block.area === area);
    if (last) last.duties.push(duty);
    else blocks.push({ area, duties: [duty] });
  }
  return blocks;
}

/**
 * personLabel — что стоит перед именем. Предлог «к» не используем: падеж из имени не вывести,
 * получалось «к Бухгалтерия». Стрелка читается одинаково с любым именем, службой и подрядчиком.
 */
export const DIRECTIONS = [
  { key: 'in' as const, title: 'Ко мне обращаются', personLabel: 'вместо меня —' },
  { key: 'out' as const, title: 'Я обращаюсь', personLabel: '→' },
];

function ContactRow({ contact, personLabel }: { contact: Contact; personLabel: string }) {
  const { open } = usePersonDrawer();
  return (
    <li className="contact-row">
      <span className="contact-topic">{contact.topic}</span>
      <span className="contact-channel">{contact.channel}</span>
      {contact.person ? (
        <span className="contact-person">
          {personLabel ? `${personLabel} ` : ''}
          {contact.personId ? (
            <button type="button" className="link" onClick={() => open(contact.personId as string)}>
              {contact.person}
            </button>
          ) : (
            contact.person
          )}
        </span>
      ) : null}
      {contact.reply ? <span className="contact-reply">ответ {contact.reply}</span> : null}
    </li>
  );
}

/**
 * Связи: сначала с чем обращаются к сотруднику, потом к кому обращается он.
 * Оба списка видны сразу; длинный список сворачивается внутри своей группы, а не целиком.
 */
export function ContactsView({ contacts, limit }: { contacts: Contact[]; limit?: number }) {
  const [opened, setOpened] = useState<string[]>([]);
  return (
    <div className="contact-groups">
      {DIRECTIONS.map((direction) => {
        const rows = contacts.filter((contact) => contact.direction === direction.key);
        if (!rows.length) return null;
        const shown = !limit || opened.includes(direction.key) ? rows : rows.slice(0, limit);
        return (
          <section key={direction.key} className="contact-group">
            <h4 className="contact-group-title">
              {direction.title} <span className="reg-count">{rows.length}</span>
            </h4>
            <ul className="contact-list">
              {shown.map((contact) => (
                <ContactRow
                  key={`${contact.direction}-${contact.topic}-${contact.person ?? ''}`}
                  contact={contact}
                  personLabel={direction.personLabel}
                />
              ))}
            </ul>
            {rows.length > shown.length ? (
              <button
                type="button"
                className="link more-contacts"
                onClick={() => setOpened([...opened, direction.key])}
              >
                Ещё {rows.length - shown.length}
              </button>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

export function CommitmentsView({ items }: { items: KpiCommitment[] }) {
  return (
    <ul className="kpi-commitments">
      {items.map((item) => (
        <li className="kpi-commitment" key={item.name}>
          <div className="kpi-commitment-head">
            <strong>{item.name}</strong>
            {item.weight === undefined ? null : <span className="kpi-weight">{item.weight} %</span>}
          </div>
          {item.weight === undefined ? null : (
            <div className="kpi-weight-bar">
              <i className="kpi-weight-fill" style={{ width: `${Math.min(item.weight, 100)}%` }} />
            </div>
          )}
          <p className="kpi-commitment-what">{item.what}</p>
          {item.measure || item.source ? (
            <small className="kpi-commitment-meta">
              {[
                item.measure ? `Считается: ${item.measure}` : null,
                item.source ? `Источник: ${item.source}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </small>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function RegulationView({ regulation }: { regulation: Regulation }) {
  const contacts = regulation.contacts ?? [];
  const commitments = regulation.kpiCommitments ?? [];
  return (
    <>
      {regulation.files.length ? (
        <div className="reg-docs">
          {regulation.files.map((file) => (
            <FileChip key={file.id} file={file} />
          ))}
        </div>
      ) : null}
      <div className="reg-columns">
        <div className="reg-part">
          <h3>
            Правила <span className="reg-count">{regulation.rules.length}</span>
          </h3>
          {regulation.rules.length ? (
            <ol className="reg-list-rows">
              {regulation.rules.map((rule, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: правила — неизменяемый список версии
                <li key={index}>
                  <span className="reg-num">{index + 1}</span>
                  <span className="reg-text">{rule}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="muted">Правил нет</p>
          )}
        </div>
        <div className="reg-part">
          <h3>
            Обязанности <span className="reg-count">{regulation.duties.length}</span>
          </h3>
          {regulation.duties.length ? (
            <div className="duty-blocks">
              {groupDuties(regulation.duties).map((block) => (
                <section key={block.area || 'без области'} className="duty-block">
                  {block.area ? <h4 className="duty-block-title">{block.area}</h4> : null}
                  <ol className="reg-list-rows">
                    {block.duties.map((duty, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: обязанности — неизменяемый список версии
                      <li key={index}>
                        <span className="reg-num">{index + 1}</span>
                        <span className="reg-text">{duty.title}</span>
                        <span className="reg-period">{duty.period}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          ) : (
            <p className="muted">Обязанностей нет</p>
          )}
        </div>
      </div>
      {contacts.length || commitments.length ? (
        <div className="reg-columns">
          {contacts.length ? (
            <div className="reg-part">
              <h3>
                Связи <span className="reg-count">{contacts.length}</span>
              </h3>
              <ContactsView contacts={contacts} />
            </div>
          ) : null}
          {commitments.length ? (
            <div className="reg-part">
              <h3>
                За что премия <span className="reg-count">{commitments.length}</span>
              </h3>
              <CommitmentsView items={commitments} />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/**
 * Адресат связи: имя вводится руками, но если оно совпало с сотрудником компании, связь
 * закрепляется именно за ним — тогда ссылка переживёт смену фамилии. Подрядчика или службу
 * («Бухгалтерия») тоже можно вписать: такая связь останется текстом.
 */
function PersonField({
  value,
  label,
  placeholder,
  onChange,
}: {
  value: string;
  label: string;
  placeholder: string;
  onChange: (person: string, personId: string | undefined) => void;
}) {
  const structure = useOrgStructure();
  const people = Object.values(structure.data?.people ?? {});
  const listId = `people-${label.replace(/\s+/g, '-')}`;
  return (
    <>
      <input
        className="input"
        list={listId}
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => {
          const person = event.target.value;
          onChange(person, people.find((item) => item.fullName === person)?.id);
        }}
      />
      <datalist id={listId}>
        {people.map((person) => (
          <option key={person.id} value={person.fullName}>
            {person.position ?? ''}
          </option>
        ))}
      </datalist>
    </>
  );
}

export const draftFrom = (regulation: Regulation | null): RegulationDraft => ({
  rules: regulation?.rules ?? [],
  duties: regulation?.duties ?? [],
  contacts: regulation?.contacts ?? [],
  kpiCommitments: regulation?.kpiCommitments ?? [],
  files: regulation?.files ?? [],
});

/** Режим «Изменить»: файлы, правила текстом (одна строка — одно правило), обязанности. */
export function RegulationEditor({
  draft,
  onChange,
}: {
  draft: RegulationDraft;
  onChange: (draft: RegulationDraft) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<unknown>(null);

  const onFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);
    try {
      const uploaded: RegulationFile[] = [];
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) throw new Error(`«${file.name}» больше 20 МБ`);
        uploaded.push(await uploadFile(file));
      }
      onChange({ ...draft, files: [...draft.files, ...uploaded] });
    } catch (error) {
      setUploadError(error);
    } finally {
      setUploading(false);
    }
  };

  const setDuty = (index: number, patch: Partial<RegulationDraft['duties'][number]>) =>
    onChange({
      ...draft,
      duties: draft.duties.map((duty, i) => (i === index ? { ...duty, ...patch } : duty)),
    });
  const setContact = (index: number, patch: Partial<Contact>) =>
    onChange({
      ...draft,
      contacts: draft.contacts.map((contact, i) => (i === index ? { ...contact, ...patch } : contact)),
    });
  const setCommitment = (index: number, patch: Partial<KpiCommitment>) =>
    onChange({
      ...draft,
      kpiCommitments: draft.kpiCommitments.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    });

  return (
    <>
      <div className="reg-docs">
        {draft.files.map((file) => (
          <FileChip
            key={file.id}
            file={file}
            onRemove={() => onChange({ ...draft, files: draft.files.filter((f) => f.id !== file.id) })}
          />
        ))}
        <label className="doc-chip add">
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            multiple
            hidden
            disabled={uploading}
            onChange={onFiles}
          />
          <Icon name="upload" />
          {uploading ? 'Загружаю…' : 'Прикрепить файл'}
        </label>
      </div>
      {uploadError ? <ErrorState title="Файл не загружен" error={uploadError} /> : null}
      <div className="reg-columns">
        <div className="reg-part">
          <h3>Правила</h3>
          <textarea
            className="input reg-textarea"
            rows={10}
            placeholder="Одно правило — одна строка"
            aria-label="Правила"
            value={draft.rules.join('\n')}
            onChange={(event) => onChange({ ...draft, rules: event.target.value.split('\n') })}
          />
        </div>
        <div className="reg-part">
          <h3>Обязанности</h3>
          <div className="reg-edit-list">
            {draft.duties.map((duty, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: строки редактора без собственных идентификаторов
              <div key={index} className="edit-row">
                <input
                  className="input duty-area-input"
                  value={duty.area ?? ''}
                  placeholder="Область: 1С, выкуп, склад"
                  aria-label={`Рабочая область ${index + 1}`}
                  onChange={(event) => setDuty(index, { area: event.target.value })}
                />
                <input
                  className="input"
                  value={duty.title}
                  placeholder="Что входит в обязанность"
                  aria-label={`Обязанность ${index + 1}`}
                  onChange={(event) => setDuty(index, { title: event.target.value })}
                />
                <select
                  className="select"
                  value={duty.period}
                  aria-label={`Периодичность ${index + 1}`}
                  onChange={(event) => setDuty(index, { period: event.target.value })}
                >
                  {DUTY_PERIODS.map((period) => (
                    <option key={period}>{period}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-icon ghost"
                  aria-label={`Удалить обязанность ${index + 1}`}
                  onClick={() => onChange({ ...draft, duties: draft.duties.filter((_, i) => i !== index) })}
                >
                  <Icon name="x" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary"
              onClick={() =>
                onChange({ ...draft, duties: [...draft.duties, { title: '', period: 'Постоянно' }] })
              }
            >
              <Icon name="plus" size={14} />
              Добавить обязанность
            </button>
          </div>
        </div>
      </div>
      <div className="reg-columns">
        <div className="reg-part">
          <h3>Связи</h3>
          <p className="muted">
            «Ко мне идут с» — с чем обращаются к сотруднику. «Я пишу» — куда он идёт сам.
          </p>
          <div className="reg-edit-list">
            {draft.contacts.map((contact, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: строки редактора без собственных идентификаторов
              <div key={index} className="edit-row contact-edit-row">
                <select
                  className="select"
                  value={contact.direction}
                  aria-label={`Направление связи ${index + 1}`}
                  onChange={(event) => setContact(index, { direction: event.target.value as 'in' | 'out' })}
                >
                  <option value="in">Ко мне идут с</option>
                  <option value="out">Я пишу</option>
                </select>
                <input
                  className="input"
                  value={contact.topic}
                  placeholder="Тема: выкуп машины, доступ к 1С"
                  aria-label={`Тема связи ${index + 1}`}
                  onChange={(event) => setContact(index, { topic: event.target.value })}
                />
                <PersonField
                  value={contact.person ?? ''}
                  label={`Кому обращаться ${index + 1}`}
                  placeholder={contact.direction === 'out' ? 'К кому: ФИО' : 'Кто вместо меня'}
                  onChange={(person, personId) => setContact(index, { person, personId })}
                />
                <select
                  className="select"
                  value={contact.channel}
                  aria-label={`Способ связи ${index + 1}`}
                  onChange={(event) => setContact(index, { channel: event.target.value })}
                >
                  {CONTACT_CHANNELS.map((channel) => (
                    <option key={channel}>{channel}</option>
                  ))}
                </select>
                <input
                  className="input"
                  value={contact.reply ?? ''}
                  placeholder="Срок ответа"
                  aria-label={`Срок ответа ${index + 1}`}
                  onChange={(event) => setContact(index, { reply: event.target.value })}
                />
                <button
                  type="button"
                  className="btn-icon ghost"
                  aria-label={`Удалить связь ${index + 1}`}
                  onClick={() =>
                    onChange({ ...draft, contacts: draft.contacts.filter((_, i) => i !== index) })
                  }
                >
                  <Icon name="x" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary"
              onClick={() =>
                onChange({
                  ...draft,
                  contacts: [...draft.contacts, { direction: 'in', topic: '', channel: CONTACT_CHANNELS[0] }],
                })
              }
            >
              <Icon name="plus" size={14} />
              Добавить связь
            </button>
          </div>
        </div>
        <div className="reg-part">
          <h3>За что премия</h3>
          <p className="muted">
            Название показателя — как в «Оклад и KPI», тогда описание встанет рядом с цифрами. Вес указывается
            либо у всех показателей и даёт 100 %, либо ни у кого.
          </p>
          <div className="reg-edit-list">
            {draft.kpiCommitments.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: строки редактора без собственных идентификаторов
              <div key={index} className="kpi-edit-row">
                <div className="kpi-edit-head">
                  <input
                    className="input"
                    value={item.name}
                    placeholder="Показатель: срок выкупа"
                    aria-label={`Показатель премии ${index + 1}`}
                    onChange={(event) => setCommitment(index, { name: event.target.value })}
                  />
                  <input
                    className="input kpi-number"
                    inputMode="numeric"
                    value={item.weight === undefined ? '' : String(item.weight)}
                    placeholder="вес %"
                    aria-label={`Вес показателя ${index + 1}`}
                    onChange={(event) => {
                      const raw = event.target.value.replace(',', '.').trim();
                      setCommitment(index, { weight: raw === '' ? undefined : Number(raw) });
                    }}
                  />
                  <button
                    type="button"
                    className="btn-icon ghost"
                    aria-label={`Удалить показатель премии ${index + 1}`}
                    onClick={() =>
                      onChange({
                        ...draft,
                        kpiCommitments: draft.kpiCommitments.filter((_, i) => i !== index),
                      })
                    }
                  >
                    <Icon name="x" />
                  </button>
                </div>
                <input
                  className="input"
                  value={item.what}
                  placeholder="Что я для этого делаю"
                  aria-label={`Что делает сотрудник ${index + 1}`}
                  onChange={(event) => setCommitment(index, { what: event.target.value })}
                />
                <div className="kpi-edit-foot">
                  <input
                    className="input"
                    value={item.measure ?? ''}
                    placeholder="Как считается"
                    aria-label={`Как считается ${index + 1}`}
                    onChange={(event) => setCommitment(index, { measure: event.target.value })}
                  />
                  <input
                    className="input"
                    value={item.source ?? ''}
                    placeholder="Откуда цифра"
                    aria-label={`Откуда цифра ${index + 1}`}
                    onChange={(event) => setCommitment(index, { source: event.target.value })}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary"
              onClick={() =>
                onChange({ ...draft, kpiCommitments: [...draft.kpiCommitments, { name: '', what: '' }] })
              }
            >
              <Icon name="plus" size={14} />
              Добавить показатель
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export const cleanDraft = (draft: RegulationDraft): RegulationDraft => ({
  files: draft.files,
  rules: draft.rules.map((rule) => rule.trim()).filter(Boolean),
  duties: draft.duties
    .map((duty) => ({ ...duty, title: duty.title.trim(), area: duty.area?.trim() || undefined }))
    .filter((duty) => duty.title),
  contacts: draft.contacts
    .map((contact) => {
      const person = contact.person?.trim() || undefined;
      return {
        ...contact,
        topic: contact.topic.trim(),
        person,
        // Имя стёрли или заменили на подрядчика — закреплённый сотрудник больше не при чём.
        personId: person ? contact.personId : undefined,
        reply: contact.reply?.trim() || undefined,
      };
    })
    .filter((contact) => contact.topic),
  kpiCommitments: draft.kpiCommitments
    .map((item) => ({
      ...item,
      name: item.name.trim(),
      what: item.what.trim(),
      measure: item.measure?.trim() || undefined,
      source: item.source?.trim() || undefined,
    }))
    .filter((item) => item.name || item.what),
});

export const isEmptyDraft = (draft: RegulationDraft) => {
  const clean = cleanDraft(draft);
  return (
    !clean.files.length &&
    !clean.rules.length &&
    !clean.duties.length &&
    !clean.contacts.length &&
    !clean.kpiCommitments.length
  );
};

export { formatDate };
